/**
 * Helpers for ECharts category x-axes so labels stay readable on narrow / mobile widths.
 */

export type CategoryAxisLayout = {
  rotate: number;
  gridBottom: string;
  fontSize: number;
};

/**
 * Split "Platform (metric)" into two lines to shorten horizontal run length.
 */
export function splitParentheticalCategoryLabel(value: string): string {
  const s = String(value ?? '').trim();
  const m = s.match(/^(.+?)\s*\(([^)]*)\)\s*$/);
  if (m) return `${m[1].trim()}\n(${m[2].trim()})`;
  return s;
}

/**
 * Break a long single-line label at a space (e.g. "Apple Music" → "Apple\nMusic").
 */
export function splitWordsOnSpace(value: string, firstLineMax = 11): string {
  const s = String(value ?? '').trim();
  if (!s || s.includes('\n')) return s;
  if (s.length <= firstLineMax) return s;
  const idx = s.lastIndexOf(' ', firstLineMax);
  if (idx <= 0) return s;
  return `${s.slice(0, idx)}\n${s.slice(idx + 1)}`;
}

/**
 * Rotation and grid bottom for categorical axes based on tick count.
 * @param mode longLabels — e.g. "Name (followers)"; use stronger rotation + margin.
 */
export function getCategoryAxisLayout(
  count: number,
  mode: 'default' | 'longLabels' = 'default'
): CategoryAxisLayout {
  if (count <= 1) {
    return { rotate: 0, gridBottom: '14%', fontSize: 10 };
  }
  if (mode === 'longLabels') {
    if (count === 2) return { rotate: 36, gridBottom: '24%', fontSize: 9 };
    if (count <= 4) return { rotate: 42, gridBottom: '30%', fontSize: 9 };
    if (count <= 6) return { rotate: 48, gridBottom: '34%', fontSize: 8 };
    return { rotate: 55, gridBottom: '38%', fontSize: 8 };
  }
  if (count === 2) return { rotate: 22, gridBottom: '18%', fontSize: 9 };
  if (count <= 4) return { rotate: 32, gridBottom: '24%', fontSize: 9 };
  if (count <= 6) return { rotate: 40, gridBottom: '28%', fontSize: 9 };
  if (count <= 10) return { rotate: 46, gridBottom: '32%', fontSize: 8 };
  return { rotate: 52, gridBottom: '36%', fontSize: 8 };
}

/** Merge percentage strings — use the larger bottom margin (string compare unreliable; use numeric). */
export function maxGridBottomPct(a: string, b: string): string {
  const pa = parseFloat(a) || 0;
  const pb = parseFloat(b) || 0;
  return `${Math.max(pa, pb)}%`;
}
